/**
 * A member's name as a link to their detail page (DNKADM-0464). A real anchor with an href (router
 * `Link`), so a plain click navigates in-app and Ctrl/Cmd/middle click opens a new tab.
 *
 * Plain text (the same {@link MemberNameText} label) when there is no `contactId` — a walk-in or
 * guest with no member page. Every operator role, STAFF included, can open member detail
 * (DNKADM-0375), so there is no role branch here.
 *
 * The link keeps the surrounding text colour, underlines on hover/focus, shows a focus ring,
 * and is 44px tall on a phone. It stops click/key propagation so a link inside a clickable
 * card or row does not also open that card's drawer.
 */
import type { ReactNode } from 'react';
import { Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MemberNameText } from '@/components/MemberNameText';
import { memberDetailPath } from '@/utils/memberPath';
import { nonEmpty } from '@/utils/personLabel';

export function MemberNameLink({
  name,
  contactId,
  fallback,
  onClick,
}: {
  name: string | null | undefined;
  contactId: string | null | undefined;
  /** Label when `name` is blank (e.g. `Reservation`); default is {@link MemberNameText}'s. */
  fallback?: string;
  /** Runs after propagation is stopped (e.g. a dialog closing as the link navigates). */
  onClick?: () => void;
}) {
  const known = nonEmpty(name);
  const label: ReactNode =
    known === null && fallback !== undefined ? (
      fallback
    ) : (
      <MemberNameText name={name} contactId={contactId} />
    );
  const id = nonEmpty(contactId);
  if (id === null) return <>{label}</>;
  return (
    <Link
      component={RouterLink}
      to={memberDetailPath(id)}
      color="inherit"
      underline="hover"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: { xs: 44, sm: 'auto' },
        maxWidth: '100%',
        minWidth: 0,
        borderRadius: 0.5,
        '&:focus-visible': {
          textDecoration: 'underline',
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {/* The label span is the flex item that ellipsizes: inside a `noWrap` parent it inherits
          `white-space: nowrap` and truncates; elsewhere it wraps as ordinary text. */}
      <Box
        component="span"
        sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'inherit' }}
      >
        {label}
      </Box>
    </Link>
  );
}
